/**
 * Confirmation gate for switching a company off deferred platform-fee billing
 * (deferred -> standard).
 *
 * Disabling is a billing-significant action: the backend immediately invoices the
 * company for any platform fees accrued in the still-open period and stops
 * deferring future transactions (they go back to being charged the platform fee
 * at the time of sale). We make the user confirm before triggering it.
 */
import { useTranslation } from 'react-i18next';

import { Modal } from '@/components/Modal/Modal';
import { Column, Row } from '@/components/SettingsLayout';
import { Button, Text } from '@/ui';

type Props = {
  open: boolean;
  isConfirming: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
};

function DeferredFeesDisableModal({ open, isConfirming, onConfirm, onCancel }: Props) {
  const { t } = useTranslation();

  return (
    <Modal open={open} closeModal={onCancel} closeOnOverlayClick={false}>
      <Modal.Title title={t('Companies.deferred_fees_disable_title')} />
      <Modal.Content>
        <Column gapSize='16'>
          <Text size='s' weight='light' color='base-grey'>
            {t('Companies.deferred_fees_disable_comment')}
          </Text>
        </Column>
      </Modal.Content>
      <Modal.Footer justify='flex-end'>
        <Row gapSize='8'>
          <Button variant='outline' type='button' onClick={onCancel} disabled={isConfirming}>
            {t('Shared.cancel')}
          </Button>
          <Button
            variant='danger'
            type='button'
            onClick={onConfirm}
            disabled={isConfirming}
            testId='deferred-fees-disable-confirm'
          >
            {t('Companies.deferred_fees_disable_confirm')}
          </Button>
        </Row>
      </Modal.Footer>
    </Modal>
  );
}

export default DeferredFeesDisableModal;
